import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  Alert,
  Paper,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  AccessTime,
  Support,
} from '@mui/icons-material';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    message: '',
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const serviceTypes = [
    'Custom Software Development (NAIC 541511)',
    'Managed IT Services (NAIC 541513)',
    'Logistics Consulting (NAIC 541614)',
    'General Inquiry',
    'Partnership Opportunity',
    'Career Inquiry',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const offices = [
    {
      location: 'Headquarters',
      address: 'Intellusia Studios LLC',
      city: 'Graham, WA 98338',
      phone: '(916) 660-3509',
    },

  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8f 0%, #3b82f6 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Contact Us
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95 }}>
            Let's discuss how Intellusia Studios can transform your technology infrastructure 
            and drive operational excellence.
          </Typography>
        </Container>
      </Box>

      {/* Contact Form & Info */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" gutterBottom>
                    Get In Touch
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Fill out the form below and our team will contact you within 24 hours.
                  </Typography>

                  {showSuccess && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      Thank you for contacting us! We'll be in touch soon.
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Company/Organization"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          select
                          label="Service Type"
                          name="serviceType"
                          value={formData.serviceType}
                          onChange={handleChange}
                          required
                        >
                          {serviceTypes.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Message"
                          name="message"
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          endIcon={<Send />}
                          sx={{ mt: 2 }}
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Contact Info */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Direct Contact
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Phone sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        (916) 660-3509
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Email sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        fredmckenzie@scriptstudio8.com
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Support sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Support
                      </Typography>
                      <Typography variant="body1">
                        support@scriptstudio8.com
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Business Hours */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Business Hours
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTime sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        24/7
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Government Contracts */}
                <Paper sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
                  <Typography variant="h6" gutterBottom>
                    Government Contracts
                  </Typography>
                  <Typography variant="body2" paragraph>
                    For government procurement inquiries:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    fredmckenzie@scriptstudio8.com
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    CAGE Code: 15GM7
                  </Typography>
                  <Typography variant="body2">
                    DUNS: 14-483-9315
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Office Locations */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom textAlign="center">
            Our Offices
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {offices.map((office) => (
              <Grid item xs={12} md={6} key={office.location}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'start' }}>
                      <LocationOn sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {office.location}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {office.address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {office.city}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {office.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom>
            Ready to Transform Your IT Infrastructure?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Schedule a free consultation with our experts today.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}
          >
            Schedule Consultation
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactPage;